import { useState } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { api, apiError } from "../lib/api";
import { Button } from "../components/ui/button";
import { Label } from "../components/ui/label";
import { PasswordInput } from "../components/PasswordInput";
import { AuthShell } from "./ForgotPassword";
import { CheckCircle, LockKey } from "@phosphor-icons/react";
import { toast } from "sonner";

export default function ResetPassword() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const navigate = useNavigate();
  const [pw, setPw] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (pw.length < 6) { toast.error("Password must be at least 6 characters"); return; }
    if (pw !== confirm) { toast.error("Passwords do not match"); return; }
    setLoading(true);
    try {
      await api.post("/auth/reset-password", { token, new_password: pw });
      setDone(true);
      setTimeout(() => navigate("/login"), 2500);
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail) || "Could not reset password");
    } finally { setLoading(false); }
  };

  if (!token) {
    return (
      <AuthShell>
        <div className="text-center" data-testid="reset-invalid">
          <h1 className="font-heading text-2xl font-bold tracking-tight">Invalid link</h1>
          <p className="text-muted-foreground text-sm mt-2">This reset link is missing or malformed. Please request a new one.</p>
          <Link to="/forgot-password" className="text-primary font-semibold text-sm mt-6 inline-block hover:underline">Request a new link</Link>
        </div>
      </AuthShell>
    );
  }

  return (
    <AuthShell>
      {done ? (
        <div className="text-center" data-testid="reset-done">
          <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mx-auto">
            <CheckCircle size={34} weight="fill" className="text-green-600" />
          </div>
          <h1 className="font-heading text-2xl font-bold tracking-tight mt-5">Password reset!</h1>
          <p className="text-muted-foreground text-sm mt-2">Redirecting you to sign in…</p>
          <Link to="/login" className="text-primary font-semibold text-sm mt-4 inline-block hover:underline" data-testid="goto-login">Go to sign in</Link>
        </div>
      ) : (
        <form onSubmit={submit} className="space-y-4" data-testid="reset-form">
          <h1 className="font-heading text-2xl font-bold tracking-tight">Set a new password</h1>
          <p className="text-muted-foreground text-sm">Choose a strong password you'll remember.</p>
          <div>
            <Label className="label-caps">New password</Label>
            <PasswordInput data-testid="reset-password" value={pw} onChange={(e) => setPw(e.target.value)} placeholder="At least 6 characters" className="rounded-sm mt-1.5" required />
          </div>
          <div>
            <Label className="label-caps">Confirm password</Label>
            <PasswordInput data-testid="reset-confirm" value={confirm} onChange={(e) => setConfirm(e.target.value)} placeholder="Re-enter password" className="rounded-sm mt-1.5" required />
          </div>
          <Button data-testid="reset-submit" type="submit" disabled={loading} className="w-full rounded-sm font-semibold gap-2">
            <LockKey size={17} weight="bold" /> {loading ? "Saving…" : "Reset password"}
          </Button>
          <p className="text-sm text-center">
            <Link to="/login" className="text-muted-foreground hover:text-foreground">← Back to sign in</Link>
          </p>
        </form>
      )}
    </AuthShell>
  );
}
